import {
  ArrowRightOutlined,
  SelectOutlined,
  SignatureFilled,
  UploadOutlined,
} from '@ant-design/icons';
import { type DettaglioFirma } from '@framework/api-client/api/inquiries-compile/schemas/dettaglioFirma.ts';
import { UploadInput } from '@framework/form/inputs/upload/UploadInput.tsx';
import { AsyncButton } from '@framework/ui/components/button/AsyncButton.tsx';
import { Flex, message, Space, type UploadFile } from 'antd';
import { type RcFile } from 'antd/es/upload';
import clx from 'classnames';
import { lighten } from 'polished';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import styled from 'styled-components';
import { SignerFileInfoRow } from './SignerFileInfoRow.tsx';
import { SignerHeader } from './SignerHeader.tsx';

const ButtonSpaceCompact = styled(Space.Compact)`
  .ant-btn-default {
    border-color: ${props => props.theme.moduleDelegateColor};
  }
  .ant-btn-default:disabled {
    background-color: ${props => props.theme.colorBgContainer};
  }
`;

const ButtonIconArrowDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  font-size: 24px;
  line-height: 24px;
  padding: 0 6px;
  color: ${props => lighten(0.4, props.theme.moduleDelegateColor)};
  background-color: ${props => lighten(0.1, props.theme.moduleDelegateColor)};
  &.is-mobile {
    width: 100%;
    height: auto;
    padding: 2px 0;
  }
`;

export interface ISignerPanelProps {
  /** Titolo per SignerHeader */
  title: string;
  /** Descrizione opzionale per SignerHeader */
  description?: React.ReactNode;
  /** Bottone download (domain-specific) */
  downloadButton: React.ReactNode;
  /** Nome input upload per accessibilita' */
  uploadName: string;
  /** File selezionato */
  file: RcFile | null;
  /** Callback selezione file */
  onFileSelect: (file: RcFile) => void;
  /** Handler upload */
  onUpload: () => Promise<void>;
  /** Disabilitato */
  disabled?: boolean;
  /** Upload in corso */
  isUploading?: boolean;
  /** File firmato caricato - mostrato tramite SignerFileInfoRow con icona FileProtectOutlined */
  signedFile?: UploadFile | null;
  /** Dati di firma associati al file firmato (per l'apertura della modal "Verifica Firma") */
  signatures?: DettaglioFirma[];
  /**
   * Estensioni file accettate per l'input di caricamento (es. `.pdf`, `.p7m`, `.pdf,.p7m`).
   * - `undefined` → default `.pdf,.p7m`
   * - `null` → nessun filtro (tutti i formati accettati)
   * - stringa → usata così com'è
   */
  accept?: string | null;
  /** Contenuto extra a destra dell'header (es. stepper) */
  extra?: React.ReactNode;
}

/**
 * Layout condiviso per il flusso di firma: header, bottoni (scarica -> freccia -> carica),
 * riga file firmato.
 *
 * NON include container/card - il wrapper resta responsabilita' del chiamante.
 */
export function SignerPanel(props: Readonly<ISignerPanelProps>) {
  const {
    title,
    description,
    downloadButton,
    uploadName,
    file,
    onFileSelect,
    onUpload,
    disabled,
    isUploading,
    signedFile,
    signatures,
    accept,
    extra,
  } = props;

  const { t } = useTranslation();
  const isMobile = useBreakpoint(down('lg'));

  const hasFile = Boolean(file || signedFile);
  const showUploadButton = !signedFile || Boolean(file);

  // `undefined` = prop non passata → fallback al default `.pdf,.p7m`.
  // `null` = nessun vincolo richiesto esplicitamente dal caller → va preservato.
  // Un semplice `accept ?? '.pdf,.p7m'` collasserebbe i due casi.
  const acceptedExtensions =
    accept === undefined ? '.pdf,.p7m' : (accept ?? null);

  return (
    <div>
      <SignerHeader
        icon={<SignatureFilled aria-hidden="true" />}
        title={title}
        description={description}
        extra={extra}
      />

      <Flex vertical gap={8}>
        <ButtonSpaceCompact
          block
          direction={isMobile ? 'vertical' : 'horizontal'}
        >
          {downloadButton}

          <ButtonIconArrowDivider className={clx({ 'is-mobile': isMobile })}>
            <ArrowRightOutlined aria-hidden="true" rotate={isMobile ? 90 : 0} />
          </ButtonIconArrowDivider>

          <UploadInput
            name={uploadName}
            title={
              hasFile
                ? t('signer.modifica-file-firmato', 'Cambia file firmato...')
                : t(
                    'signer.seleziona-file-firmato',
                    'Seleziona file firmato...',
                  )
            }
            showUploadList={false}
            accept={acceptedExtensions ?? undefined}
            disabled={disabled || isUploading}
            beforeUpload={file => {
              const allowedExtensions = acceptedExtensions
                ? acceptedExtensions
                    .split(',')
                    .map(ext => ext.trim())
                    .filter(Boolean)
                : [];

              const fileName = file.name.toLowerCase();
              const isValid =
                allowedExtensions.length === 0 ||
                allowedExtensions.some(ext => fileName.endsWith(ext));

              if (isValid) {
                onFileSelect(file);
              } else {
                message.error(
                  t(
                    'signer.formato-file-non-valido',
                    'Formato file non valido. Estensioni accettate: {{extensions}}',
                    { extensions: allowedExtensions.join(', ') },
                  ),
                );
              }
              return false;
            }}
            buttonProps={{
              icon: <SelectOutlined aria-hidden="true" />,
              disabled: disabled || isUploading,
              loading: isUploading,
            }}
          />
        </ButtonSpaceCompact>

        <Flex align="center" gap={8} vertical={isMobile ?? false}>
          <SignerFileInfoRow
            file={file}
            signedFile={signedFile}
            signatures={signatures}
          />

          {showUploadButton && (
            <AsyncButton
              block={isMobile ?? false}
              size="large"
              type="primary"
              icon={<UploadOutlined aria-hidden="true" />}
              onClick={onUpload}
              loading={isUploading}
              disabled={!file || disabled || isUploading}
            >
              {t('signer.carica-e-completa', 'Carica e Completa')}
            </AsyncButton>
          )}
        </Flex>
      </Flex>
    </div>
  );
}
